// dynamics-sim-v0 reader: described arrays (`header.arrays`) on named time bases. The app reads the
// region series (`parcel_activity`, visual rate) for overlays and the sensor arrays (`eeg`, `seeg`,
// signal rate, volts) for traces; the engine reads the vertex arrays itself.

import type { Typed } from './binary.ts';
import type { SimFiles } from './files.ts';
import { axisFromHeader, axisFromTimeBase, type TimeAxis } from './time.ts';
import type { DynamicsHeader, DynamicsParcel } from './types.ts';

export type SensorKind = 'eeg' | 'seeg';

export interface ChannelGroup {
  id: string;
  label: string;
  /** Column indices into the sensor array, in header order. */
  channels: number[];
}

const PREFIX = /^([A-Za-z]+?)(?:\d+|z)?$/;

/**
 * Groups for a stacked strip. SEEG groups by electrode (the letters before the contact number:
 * LT01…LT08 → LT). Scalp EEG groups by hemisphere from the 10–20 rule: odd numbers left, even
 * right, `z` midline.
 */
export function channelGroups(kind: SensorKind, names: string[]): ChannelGroup[] {
  const groups = new Map<string, ChannelGroup>();
  const add = (id: string, label: string, i: number) => {
    const g = groups.get(id) ?? { id, label, channels: [] };
    g.channels.push(i);
    groups.set(id, g);
  };
  names.forEach((name, i) => {
    if (kind === 'seeg') {
      const p = PREFIX.exec(name)?.[1] ?? name;
      add(p, p, i);
    } else {
      const last = name.at(-1) ?? '';
      if (/z/i.test(last)) add('mid', 'Midline', i);
      else if (/\d/.test(last)) Number(last) % 2 ? add('left', 'Left', i) : add('right', 'Right', i);
      else add('other', 'Other', i);
    }
  });
  return [...groups.values()];
}

export class DynamicsSim {
  /** The rate the cortex is drawn at (`time.timebase`, default `visual`). */
  readonly visual: TimeAxis;
  readonly parcels: DynamicsParcel[];

  private constructor(
    readonly files: SimFiles,
    readonly header: DynamicsHeader,
  ) {
    const name = header.time?.timebase ?? 'visual';
    const tb = header.time_bases?.[name];
    this.visual = tb ? axisFromTimeBase(tb) : axisFromHeader(header.time);
    this.parcels = header.parcels ?? [];
  }

  static async open(files: SimFiles, header: DynamicsHeader): Promise<DynamicsSim> {
    return new DynamicsSim(files, header);
  }

  has(name: string): boolean {
    return !!this.header.arrays?.[name];
  }

  /** The time base an array runs on. */
  axisOf(name: string): TimeAxis {
    const tbName = this.header.arrays?.[name]?.timebase;
    const tb = tbName ? this.header.time_bases?.[tbName] : undefined;
    return tb ? axisFromTimeBase(tb) : this.visual;
  }

  async array(name: string): Promise<Typed> {
    const d = this.header.arrays?.[name];
    if (!d) throw new Error(`${this.header.id} has no ${name} array`);
    return this.files.array(d);
  }

  channels(kind: SensorKind): string[] {
    return this.header.channels?.[kind] ?? [];
  }

  /** Sensor potentials `[sample][channel]` in volts, on the signal time base. */
  async sensors(kind: SensorKind): Promise<{ data: Float32Array; channels: string[]; axis: TimeAxis; units: string }> {
    const d = this.header.arrays?.[kind];
    if (!d) throw new Error(`${this.header.id} has no ${kind} array`);
    const channels = this.channels(kind);
    if (d.shape[1] !== channels.length) throw new Error(`${kind}: ${d.shape[1]} columns for ${channels.length} channels`);
    const data = (await this.array(kind)) as Float32Array;
    return { data, channels, axis: this.axisOf(kind), units: d.units ?? this.header.units?.potential ?? 'V' };
  }

  /**
   * Region activity keyed by asset-pack structure, `[frame][key]`, on the visual time base. Parcels
   * without a structure are skipped; two parcels on one structure keep the larger value.
   */
  async structureSeries(): Promise<{ keys: string[]; values: Float32Array; axis: TimeAxis; relation: Map<string, string> }> {
    const d = this.header.arrays?.parcel_activity;
    if (!d) throw new Error(`${this.header.id} has no parcel_activity array`);
    const P = d.shape[1] ?? 0;
    if (P !== this.parcels.length) throw new Error(`parcel_activity has ${P} columns for ${this.parcels.length} parcels`);
    const data = (await this.array('parcel_activity')) as Float32Array;
    const keyOf = new Map<string, number>();
    const keys: string[] = [];
    const relation = new Map<string, string>();
    const col = this.parcels.map((p) => {
      if (!p.structure) return -1;
      let k = keyOf.get(p.structure);
      if (k === undefined) {
        k = keys.push(p.structure) - 1;
        keyOf.set(p.structure, k);
        if (p.relation) relation.set(p.structure, p.relation);
      }
      return k;
    });
    const F = d.shape[0] ?? 0;
    const K = keys.length;
    const values = new Float32Array(F * K).fill(-Infinity);
    for (let f = 0; f < F; f++)
      for (let p = 0; p < P; p++) {
        const k = col[p]!;
        if (k >= 0) values[f * K + k] = Math.max(values[f * K + k]!, data[f * P + p]!);
      }
    return { keys, values, axis: this.axisOf('parcel_activity'), relation };
  }

  /** The suggested emission range (`display.activity_range`). */
  get activityRange(): [number, number] | null {
    return this.header.display?.activity_range ?? null;
  }
}
